import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { DragDropContext, Droppable, Draggable, DropResult } from "@hello-pangea/dnd";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Search, Users, Calendar, MoreVertical, Trash2, Building2, GripVertical } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { format } from "date-fns";
import { ptBR } from "date-fns/locale";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { WorkflowMembersDialog } from "./WorkflowMembersDialog";
import { BoardDeleteDialog } from "./BoardDeleteDialog";
import { toast } from "@/lib/toast";

// Define the Board interface
interface Board {
  id: string;
  name: string;
  description?: string;
  client?: { id: string; name: string; logo_url?: string } | null;
  created_at: string;
  updated_at: string;
  is_archived: boolean;
  user_id: string;
  client_id: string;
  position?: number;
}

interface BoardListTabsProps {
  selectedBoard: Board | null;
  onBoardSelect: (board: Board) => void;
  boards: Board[];
  onRefresh: () => void;
}

export function BoardListTabs({
  selectedBoard,
  onBoardSelect,
  boards,
  onRefresh
}: BoardListTabsProps) {
  const [searchTerm, setSearchTerm] = useState("");
  const [membersDialogBoard, setMembersDialogBoard] = useState<string | null>(null);
  const [deleteDialogBoard, setDeleteDialogBoard] = useState<Board | null>(null);
  const [deletingBoard, setDeletingBoard] = useState(false);
  const queryClient = useQueryClient();

  // Get client logos for boards
  const { data: clientLogos = {} } = useQuery({
    queryKey: ["boards-client-logos", boards.map(b => b.client_id).filter(Boolean)],
    queryFn: async () => {
      const clientIds = boards
        .map(board => board.client_id)
        .filter(Boolean)
        .filter((id, index, arr) => arr.indexOf(id) === index); // Remove duplicates
      
      if (!clientIds.length) return {};
      
      const { data: clients } = await supabase
        .from("clients")
        .select("id, logo_url")
        .in("id", clientIds);
      
      const logos: Record<string, string | null> = {};
      clients?.forEach(client => {
        logos[client.id] = client.logo_url;
      });
      
      return logos;
    },
    enabled: boards.some(board => board.client_id)
  });

  // Get cards count for each board
  const { data: cardsCount = {} } = useQuery({
    queryKey: ["boards-cards-count", boards.map(b => b.id)],
    queryFn: async () => {
      if (!boards.length) return {};
      
      const boardIds = boards.map(board => board.id);
      const { data: columns } = await supabase
        .from("kanban_columns")
        .select("id, board_id")
        .in("board_id", boardIds);
      
      if (!columns?.length) return {};
      
      const columnIds = columns.map(col => col.id);
      const { data: cards } = await supabase
        .from("kanban_cards")
        .select("column_id")
        .in("column_id", columnIds)
        .eq("is_archived", false);
      
      // Count cards per board
      const counts: Record<string, number> = {};
      boards.forEach(board => {
        const boardColumns = columns.filter(col => col.board_id === board.id);
        const boardColumnIds = boardColumns.map(col => col.id);
        const boardCards = cards?.filter(card => boardColumnIds.includes(card.column_id)) || [];
        counts[board.id] = boardCards.length;
      });
      
      return counts;
    },
    enabled: boards.length > 0
  });

  // Mutation to update board positions
  const updateBoardPositionMutation = useMutation({
    mutationFn: async (boardsToUpdate: { id: string; position: number }[]) => {
      console.log('Updating board positions:', boardsToUpdate);
      
      // Update each board position in the database
      for (const board of boardsToUpdate) {
        const { error } = await supabase
          .from('kanban_boards')
          .update({ position: board.position })
          .eq('id', board.id);
        
        if (error) {
          console.error('Error updating board position:', error);
          throw error;
        }
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["kanban-boards"] });
      onRefresh();
      toast.success('Ordem dos quadros atualizada!');
    },
    onError: (error) => {
      console.error('Error updating board positions:', error);
      toast.error('Erro ao atualizar ordem dos quadros');
    }
  });

  // Handle drag end for boards
  const handleDragEnd = (result: DropResult) => {
    const { destination, source } = result;

    if (!destination) return;
    if (destination.index === source.index) return;

    // Create a copy of the filtered boards array
    const reorderedBoards = Array.from(filteredBoards);
    const [movedBoard] = reorderedBoards.splice(source.index, 1);
    reorderedBoards.splice(destination.index, 0, movedBoard);

    // Calculate new positions
    const boardsToUpdate = reorderedBoards.map((board, index) => ({
      id: board.id,
      position: index + 1
    }));

    // Update positions in database
    updateBoardPositionMutation.mutate(boardsToUpdate);
  };

  const handleDeleteBoard = async () => {
    if (!deleteDialogBoard) return;
    
    setDeletingBoard(true);
    try {
      console.log("Excluindo quadro:", deleteDialogBoard.id);
      
      // Archive the board instead of deleting
      const { error } = await supabase
        .from('kanban_boards')
        .update({ is_archived: true })
        .eq('id', deleteDialogBoard.id);
        
      if (error) {
        console.error("Erro ao excluir quadro:", error);
        throw error;
      }
      
      toast.success("Quadro excluído com sucesso!");
      setDeleteDialogBoard(null);
      onRefresh();
    } catch (error) {
      console.error("Erro ao excluir quadro:", error);
      toast.error("Erro ao excluir quadro");
    } finally {
      setDeletingBoard(false);
    }
  };

  const filteredBoards = boards
    .filter(board =>
      board.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      board.description?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      board.client?.name?.toLowerCase().includes(searchTerm.toLowerCase())
    )
    .sort((a, b) => (a.position || 0) - (b.position || 0)); // Sort by position

  // Function to get client initials for fallback
  const getClientInitials = (clientName?: string) => {
    if (!clientName) return "QB";
    return clientName
      .split(" ")
      .map(word => word.charAt(0))
      .join("")
      .toUpperCase()
      .slice(0, 2);
  };

  // Function to render client logo or fallback
  const renderClientLogo = (board: Board) => {
    const logoUrl = board.client_id ? clientLogos[board.client_id] : null;
    
    if (logoUrl) {
      return (
        <img
          src={logoUrl}
          alt={`Logo ${board.client?.name || 'Cliente'}`}
          className="w-full h-full object-cover"
        />
      );
    }

    if (board.client) {
      return (
        <div className="w-full h-full bg-primary/10 flex items-center justify-center">
          <div className="w-16 h-16 rounded-lg bg-primary text-primary-foreground flex items-center justify-center text-xl font-bold">
            {getClientInitials(board.client.name)}
          </div>
        </div>
      );
    }

    return (
      <div className="w-full h-full bg-muted flex items-center justify-center">
        <Building2 className="h-12 w-12 text-muted-foreground" />
      </div>
    );
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-4">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-4 w-4" />
          <Input
            placeholder="Buscar quadros..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-10"
          />
        </div>
      </div>

      {filteredBoards.length === 0 ? (
        <div className="text-center py-12">
          <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100 mb-2">
            {searchTerm ? "Nenhum quadro encontrado" : "Nenhum quadro criado"}
          </h3>
          <p className="text-gray-500 dark:text-gray-400">
            {searchTerm 
              ? "Tente alterar os termos de busca."
              : "Crie seu primeiro quadro para começar a organizar tarefas."
            }
          </p>
        </div>
      ) : (
        <DragDropContext onDragEnd={handleDragEnd}>
          <Droppable droppableId="boards" direction="horizontal">
            {(provided) => (
              <div
                {...provided.droppableProps}
                ref={provided.innerRef}
                className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6 gap-4"
              >
                {filteredBoards.map((board, index) => (
                  <Draggable key={board.id} draggableId={board.id} index={index}>
                    {(provided, snapshot) => (
                      <Card 
                        ref={provided.innerRef}
                        {...provided.draggableProps}
                        className={`cursor-pointer transition-all hover:shadow-lg group aspect-square ${
                          selectedBoard?.id === board.id ? 'ring-2 ring-primary' : ''
                        } ${snapshot.isDragging ? 'opacity-50 scale-105' : ''}`}
                        onClick={() => onBoardSelect(board)}
                      >
                        {/* Client Logo/Cover */}
                        <div className="relative h-32 bg-gradient-to-br from-primary/20 to-primary/5 rounded-t-lg flex items-center justify-center overflow-hidden">
                          {renderClientLogo(board)}
                          
                          {/* Drag Handle */}
                          <div 
                            {...provided.dragHandleProps}
                            className="absolute top-2 left-2 cursor-grab active:cursor-grabbing p-1 rounded bg-background/80 hover:bg-background opacity-0 group-hover:opacity-100 transition-opacity"
                            onClick={(e) => e.stopPropagation()}
                          >
                            <GripVertical className="h-3 w-3 text-muted-foreground" />
                          </div>
                          
                          {/* Dropdown Menu */}
                          <DropdownMenu>
                            <DropdownMenuTrigger asChild onClick={(e) => e.stopPropagation()}>
                              <Button 
                                variant="ghost" 
                                size="sm"
                                className="absolute top-2 right-2 h-6 w-6 p-0 opacity-0 group-hover:opacity-100 transition-opacity bg-background/80 hover:bg-background"
                              >
                                <MoreVertical className="h-3 w-3" />
                              </Button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent align="end">
                              <DropdownMenuItem 
                                onClick={(e) => {
                                  e.stopPropagation();
                                  setMembersDialogBoard(board.id);
                                }}
                              >
                                <Users className="h-4 w-4 mr-2" />
                                Gerenciar Membros
                              </DropdownMenuItem>
                              <DropdownMenuItem 
                                onClick={(e) => {
                                  e.stopPropagation();
                                  setDeleteDialogBoard(board);
                                }}
                                className="text-red-600"
                              >
                                <Trash2 className="h-4 w-4 mr-2" />
                                Excluir Quadro
                              </DropdownMenuItem>
                            </DropdownMenuContent>
                          </DropdownMenu>
                        </div>

                        {/* Board Info */}
                        <CardContent className="p-3 space-y-2">
                          <div>
                            <CardTitle className="text-sm font-medium truncate leading-tight">
                              {board.name}
                            </CardTitle>
                            {board.client && (
                              <CardDescription className="text-xs truncate">
                                {board.client.name}
                              </CardDescription>
                            )}
                          </div>

                          <div className="flex items-center justify-between">
                            <div className="flex items-center gap-1 text-xs text-muted-foreground">
                              <Calendar className="h-3 w-3" />
                              {format(new Date(board.created_at), "dd/MM", { locale: ptBR })}
                            </div>
                            <Badge variant="secondary" className="text-xs px-1.5 py-0.5">
                              {cardsCount[board.id] || 0}
                            </Badge>
                          </div>
                        </CardContent>
                      </Card>
                    )}
                  </Draggable>
                ))}
                {provided.placeholder}
              </div>
            )}
          </Droppable>
        </DragDropContext>
      )}

      {/* Members Dialog */}
      {membersDialogBoard && (
        <WorkflowMembersDialog 
          boardId={membersDialogBoard}
          boardName={boards.find(b => b.id === membersDialogBoard)?.name || ""}
          open={!!membersDialogBoard}
          onOpenChange={(open) => !open && setMembersDialogBoard(null)}
        />
      )}

      {/* Delete Dialog */}
      {deleteDialogBoard && (
        <BoardDeleteDialog
          open={!!deleteDialogBoard}
          onOpenChange={(open) => !open && setDeleteDialogBoard(null)}
          onConfirm={handleDeleteBoard}
          boardName={deleteDialogBoard.name}
          loading={deletingBoard}
        />
      )}
    </div>
  );
}
